import type { ConversationAttachment } from './direct-call-attachments';

export interface DirectCallMessage {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  senderInstanceId?: string;
  turnId?: number;
  humanTurnId?: number;
  memberSetRevision?: number;
  /** Authenticated resume snapshot: display as text, never enqueue speech. */
  historical?: boolean;
  /** WI 35510: files on this turn. Shown, never spoken (speech reads `content` only). */
  attachments?: readonly ConversationAttachment[];
}

export interface DirectCallThread {
  id: string;
  title: string;
  updatedAt: string;
  members: readonly string[];
}

export interface DirectCallConnection {
  readonly id: string;
  readonly members: readonly string[];
  /** Story 35511: every seat that has been a member (accepts a removed seat's committed reply). */
  readonly seats?: readonly string[];
  /** Story 35511: server-recorded membership changes, oldest first. */
  readonly memberChanges?: readonly DirectCallMemberChange[];
  /** Story 35511: replace the members of a live call; resolves the confirmed set. */
  setMembers?(
    members: readonly string[],
    signal: AbortSignal
  ): Promise<readonly string[]>;
  /** to: the member seats a group turn is addressed to (Bug 35509); omitted means everyone. */
  send(
    content: string,
    signal: AbortSignal,
    to?: readonly string[],
    attachmentIds?: readonly string[]
  ): Promise<DirectCallMessage>;
  subscribe(
    receive: (message: DirectCallMessage) => void,
    failed: () => void,
    membersChanged?: () => void
  ): () => void;
  // Stop delivery and release transport resources, not delete server history.
  close(): void;
}

// Platform adapters must use server-owned human identity and private member
// delivery. This port does not authorize requests or define the HTTP wire format.
export interface DirectCallTransport {
  listThreads?(signal: AbortSignal): Promise<readonly DirectCallThread[]>;
  resume?(
    conversationId: string,
    signal: AbortSignal
  ): Promise<DirectCallConnection>;
  connect(
    members: readonly string[],
    signal: AbortSignal
  ): Promise<DirectCallConnection>;
}

/** Story 35511: one owner membership change in the middle of a call. */
export interface DirectCallMemberChange {
  revision: number;
  afterTurnId: number;
  at: string;
  added: readonly string[];
  removed: readonly string[];
}

export type DirectCallSessionState = 'idle' | 'connecting' | 'active' | 'ended';
export type DirectCallSessionFailure =
  'connect' | 'delivery' | 'send-unknown' | null;

export class DirectCallSession {
  state: DirectCallSessionState = 'idle';
  failure: DirectCallSessionFailure = null;
  sending = false;
  private connection: DirectCallConnection | null = null;
  private operation = new AbortController();
  private unsubscribe: (() => void) | undefined;
  private transcript = new Map<string, DirectCallMessage>();

  constructor(
    private readonly transport: DirectCallTransport,
    private readonly changed: () => void = () => undefined
  ) {}

  get messages(): readonly DirectCallMessage[] {
    return [...this.transcript.values()].map((message) => ({ ...message }));
  }

  get conversationId(): string | null {
    return this.connection?.id ?? null;
  }

  get members(): readonly string[] {
    return [...(this.connection?.members ?? [])];
  }

  get memberChanges(): readonly DirectCallMemberChange[] {
    return [...(this.connection?.memberChanges ?? [])];
  }

  /**
   * Story 35511: replace the seats of the live call. Resolves false (the old
   * set intact) when the call is not active, a send is in flight, the set is
   * invalid, or the server refuses it.
   */
  async setMembers(members: readonly string[]): Promise<boolean> {
    const connection = this.connection;
    const next = [...new Set(members)];
    if (
      this.state !== 'active' ||
      !connection?.setMembers ||
      this.sending ||
      !next.length ||
      next.length > 20 ||
      next.some((id) => typeof id !== 'string' || !id.trim())
    )
      return false;
    try {
      await connection.setMembers(next, this.operation.signal);
      return this.connection === connection && this.state === 'active';
    } catch {
      return false;
    } finally {
      this.changed();
    }
  }

  get awaitingSeats(): readonly string[] {
    if (this.state !== 'active' || this.failure || !this.connection) return [];
    const messages = this.messages;
    const humanTurnId = messages.reduce(
      (latest, message) =>
        message.role === 'user' && !message.historical
          ? Math.max(latest, message.humanTurnId ?? 0)
          : latest,
      0
    );
    if (!humanTurnId) return [];
    return this.connection.members.filter(
      (seat) =>
        !messages.some(
          (message) =>
            message.role === 'assistant' &&
            message.senderInstanceId === seat &&
            message.humanTurnId === humanTurnId
        )
    );
  }

  async start(members: readonly string[]): Promise<boolean> {
    if (this.state !== 'idle') return false;
    const selected = [...new Set(members)];
    if (
      !selected.length ||
      selected.length > 20 ||
      selected.some((id) => !id.trim())
    )
      return false;
    return this.open(
      () => this.transport.connect(selected, this.operation.signal),
      selected
    );
  }

  async resume(conversationId: string): Promise<boolean> {
    if (
      this.state !== 'idle' ||
      !this.transport.resume ||
      !/^[a-zA-Z0-9_-]+$/.test(conversationId)
    )
      return false;
    return this.open(
      () => this.transport.resume!(conversationId, this.operation.signal),
      undefined,
      conversationId
    );
  }

  private async open(
    connect: () => Promise<DirectCallConnection>,
    selected?: readonly string[],
    expectedId?: string
  ): Promise<boolean> {
    this.state = 'connecting';
    this.failure = null;
    this.changed();
    let connection: DirectCallConnection | undefined;
    try {
      connection = await connect();
      if (this.operation.signal.aborted) {
        const lateConnection = connection;
        connection = undefined;
        lateConnection.close();
        return false;
      }
      const confirmed = new Set(connection.members);
      if (
        !connection.id.trim() ||
        (expectedId !== undefined && connection.id !== expectedId) ||
        confirmed.size !== connection.members.length ||
        confirmed.size > 20 ||
        connection.members.some((id) => !id.trim()) ||
        (selected !== undefined &&
          (confirmed.size !== selected.length ||
            selected.some((id) => !confirmed.has(id))))
      )
        throw new Error('Conversation membership was not confirmed');
      this.connection = connection;
      const activeConnection = connection;
      connection = undefined;
      this.state = 'active';
      const unsubscribe = activeConnection.subscribe(
        (message) => this.receive(message),
        () => {
          if (this.state !== 'active') return;
          this.failure = 'delivery';
          this.changed();
        },
        () => {
          if (this.state === 'active') this.changed();
        }
      );
      // A replay callback can synchronously end the call before subscribe returns.
      if (this.operation.signal.aborted) {
        unsubscribe();
        return false;
      }
      this.unsubscribe = unsubscribe;
      this.changed();
      return !this.operation.signal.aborted;
    } catch {
      const failedConnection = this.connection ?? connection;
      this.connection = null;
      this.transcript.clear();
      try {
        failedConnection?.close();
      } catch {
        // Cleanup must not strand a failed start or expose transport details.
      }
      if (this.operation.signal.aborted) return false;
      this.state = 'idle';
      this.failure = 'connect';
      this.changed();
      return false;
    }
  }

  async send(
    content: string,
    to?: readonly string[],
    attachmentIds: readonly string[] = []
  ): Promise<boolean> {
    const connection = this.connection;
    if (
      this.state !== 'active' ||
      !connection ||
      this.sending ||
      this.failure ||
      (!content.trim() && !attachmentIds.length)
    )
      return false;
    this.sending = true;
    this.changed();
    try {
      const message = await connection.send(
        content,
        this.operation.signal,
        to,
        attachmentIds
      );
      if (this.operation.signal.aborted) return false;
      if (message.role !== 'user' || !this.receive(message))
        throw new Error('Send was not confirmed');
      return true;
    } catch {
      // A failed response does not prove the server rejected the write. Never
      // automatically retry and duplicate a spoken/user turn without reconciliation.
      if (!this.operation.signal.aborted) this.failure = 'send-unknown';
      return false;
    } finally {
      this.sending = false;
      this.changed();
    }
  }

  private receive(message: DirectCallMessage): boolean {
    if (
      this.state !== 'active' ||
      !this.connection ||
      message.conversationId !== this.connection.id ||
      !message.id.trim() ||
      (!message.content.trim() && !message.attachments?.length) ||
      (message.role !== 'user' && message.role !== 'assistant') ||
      (message.role === 'assistant' &&
        (!message.senderInstanceId ||
          (!message.historical &&
            !(this.connection.seats ?? this.connection.members).includes(
              message.senderInstanceId
            ))))
    )
      return false;
    const existing = this.transcript.get(message.id);
    if (existing)
      return (
        existing.content === message.content &&
        existing.role === message.role &&
        existing.senderInstanceId === message.senderInstanceId
      );
    this.transcript.set(message.id, { ...message });
    this.changed();
    return true;
  }

  end(): void {
    if (this.state === 'ended') return;
    this.state = 'ended';
    const unsubscribe = this.unsubscribe;
    const connection = this.connection;
    this.unsubscribe = undefined;
    this.connection = null;
    this.transcript.clear();
    this.failure = null;
    this.sending = false;
    this.operation.abort();
    try {
      unsubscribe?.();
    } catch {
      this.failure = 'delivery';
    }
    try {
      connection?.close();
    } catch {
      this.failure = 'delivery';
    }
    this.changed();
  }
}
